// src/view/assets.ts — Varlık boru hattı (ARCHITECTURE §6): GLTF promise önbelleği, karakter klonu (SkeletonUtils), prop geometrisi +
// 5 sınıf materyal/doku paylaşımı, Platformer kit geometrisi (materyal sökme + tek kit materyali), clipdata.json fetch, yükleme ilerlemesi.
// Yollar `/assets/...` (public symlink); `import.meta.env.BASE_URL` öneki burada eklenir (Node testinde env yok → '/').
// parseClipData (anim/clipData.ts, WP1) DOSYA YOLU import edilmez: `fetchClipData(url, parse)` ayrıştırıcıyı enjeksiyonla alır (WP9 bağlar).
import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/addons/utils/SkeletonUtils.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { CharId, PropId } from '../core/types.ts';
import { TEXTURE_URLS, type CharacterDef, type TexClass } from '../config/characters.ts';
import { T } from '../config/tuning.ts';

/* ------------------------------------------------------------------ yollar */

/** Vite BASE_URL öneki; Node (tsx --test) altında import.meta.env yok → '/'. */
const BASE_URL: string = (() => {
  const env = (import.meta as unknown as { env?: { BASE_URL?: string } }).env;
  return env?.BASE_URL ?? '/';
})();
/** '/assets/...' → BASE_URL + yol (çift eğik çizgi olmadan). */
export function assetUrl(path: string): string {
  const base = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
  return path.startsWith('/') ? base + path : base + '/' + path;
}

/** Prop gltf yolları — assets/viewer/data/catalog.json props[] (elle kopyalandı; view.test katalogla çapraz doğrular). */
const PROP_DIR = '/assets/KayKit_Adventurers_2.0_FREE/Assets/gltf/';
export const PROP_URLS: Readonly<Record<PropId, string>> = {
  sword_1handed: PROP_DIR + 'sword_1handed.gltf', axe_1handed: PROP_DIR + 'axe_1handed.gltf',
  sword_2handed: PROP_DIR + 'sword_2handed.gltf', sword_2handed_color: PROP_DIR + 'sword_2handed_color.gltf',
  axe_2handed: PROP_DIR + 'axe_2handed.gltf', dagger: PROP_DIR + 'dagger.gltf', staff: PROP_DIR + 'staff.gltf',
  shield_round: PROP_DIR + 'shield_round.gltf', shield_round_color: PROP_DIR + 'shield_round_color.gltf',
  shield_square: PROP_DIR + 'shield_square.gltf', shield_square_color: PROP_DIR + 'shield_square_color.gltf',
  shield_spikes: PROP_DIR + 'shield_spikes.gltf', shield_spikes_color: PROP_DIR + 'shield_spikes_color.gltf',
  shield_badge: PROP_DIR + 'shield_badge.gltf', shield_badge_color: PROP_DIR + 'shield_badge_color.gltf',
};
/** Prop → doku sınıfı (katalog `material`): 5 atlas → 5 paylaşımlı MeshStandardMaterial (ARCHITECTURE §6). */
export const PROP_CLASS: Readonly<Record<PropId, TexClass>> = {
  sword_1handed: 'knight', axe_1handed: 'barbarian', sword_2handed: 'knight', sword_2handed_color: 'knight', axe_2handed: 'barbarian',
  dagger: 'rogue', staff: 'mage',
  shield_round: 'knight', shield_round_color: 'knight', shield_square: 'knight', shield_square_color: 'knight',
  shield_spikes: 'knight', shield_spikes_color: 'knight', shield_badge: 'knight', shield_badge_color: 'knight',
};
export const ALL_PROP_IDS: readonly PropId[] = Object.keys(PROP_URLS) as PropId[];

/** Platformer kiti (katalog kits[platformer]): model id → gltf yolu; atlas tek. */
const KIT_DIR = '/assets/KayKit_Platformer_Pack_1.0_FREE/';
export const KIT_TEXTURE_URL = KIT_DIR + 'Textures/platformer_texture.png';
export const kitModelUrl = (modelId: string): string => `${KIT_DIR}Assets/gltf/${modelId}.gltf`;

/** Portre (HUD köşe kartı; katalog `sample`); Mannequin_Medium'da yok → HUD baş harf gösterir. */
/** Portreler OYUNUN KENDİ modellerinden üretiliyor (`?portraits=1` + tools/make-portraits.mjs).
 *  Eskiden KayKit'in TANITIM AFİŞLERİ kullanılıyordu: 1920×1080, logo + yazı + silah rafı + iki poz;
 *  küçük kareye `cover` ile kırpılınca rastgele bir dilim görünüyordu, Kapüşonlu Haydut ile Haydut aynı
 *  afişi paylaşıyordu ve Manken'in hiç resmi yoktu. Artık seçtiğin şey ekranda göreceğin şey. */
const PORTRAITS = '/assets/portraits/';
export const PORTRAIT_URLS: Readonly<Record<CharId, string | null>> = {
  Knight: PORTRAITS + 'Knight.png', Barbarian: PORTRAITS + 'Barbarian.png', Mage: PORTRAITS + 'Mage.png',
  Rogue: PORTRAITS + 'Rogue.png', Rogue_Hooded: PORTRAITS + 'Rogue_Hooded.png', Ranger: PORTRAITS + 'Ranger.png',
  Skeleton_Warrior: PORTRAITS + 'Skeleton_Warrior.png', Skeleton_Rogue: PORTRAITS + 'Skeleton_Rogue.png',
  Skeleton_Mage: PORTRAITS + 'Skeleton_Mage.png', Skeleton_Minion: PORTRAITS + 'Skeleton_Minion.png',
  Mannequin_Medium: PORTRAITS + 'Mannequin_Medium.png',
};

/* --------------------------------------------------------------- ilerleme */

/** Yükleme ekranı ilerlemesi: tamamlanan / istek (ARCHITECTURE §6). WP9 menü DOM'una bağlar. */
export class LoadProgress {
  total = 0; done = 0; label = '';
  onChange: ((p: LoadProgress) => void) | null = null;
  get fraction(): number { return this.total === 0 ? 1 : this.done / this.total; }
  track<X>(p: Promise<X>, label: string): Promise<X> {
    this.total++;
    this.label = label;
    this.onChange?.(this);
    return p.then((v) => { this.done++; this.label = label; this.onChange?.(this); return v; },
      (err: unknown) => { this.done++; this.onChange?.(this); throw err; });
  }
}

/* ------------------------------------------------------------- GLTF önbellek */

const loader = new GLTFLoader();
const gltfCache = new Map<string, Promise<GLTF>>();

/** GLTF promise önbelleği (url → Promise<GLTF>); menüye dönüşte önbellek kalır. */
export function loadGLTF(url: string): Promise<GLTF> {
  let p = gltfCache.get(url);
  if (!p) {
    p = new Promise<GLTF>((res, rej) => loader.load(assetUrl(url), res, undefined, rej));
    gltfCache.set(url, p);
  }
  return p;
}
export const isGltfLoaded = (url: string): boolean => gltfCache.has(url);

/** Karakter: GLTF önbelleği + SkeletonUtils.clone (skinned mesh için şart); gölge açık, frustumCulled kapalı (bbox güncellenmez). */
export async function loadCharacter(def: CharacterDef): Promise<THREE.Object3D> {
  const gltf = await loadGLTF(def.url);
  return instantiateCharacter(gltf.scene);
}
export function instantiateCharacter(template: THREE.Object3D): THREE.Object3D {
  const root = skeletonClone(template);
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; m.frustumCulled = false; }
  });
  return root;
}

/* ------------------------------------------------------- sınıf materyalleri */

const texLoader = new THREE.TextureLoader();
const classMaterials = new Map<TexClass, THREE.MeshStandardMaterial>();
/** Aynı atlası paylaşan tüm proplar tek MeshStandardMaterial kullanır (5 sınıf + iskelet/manken). */
export function classMaterial(cls: TexClass): THREE.MeshStandardMaterial {
  let m = classMaterials.get(cls);
  if (!m) {
    const tex = texLoader.load(assetUrl(TEXTURE_URLS[cls]));
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.flipY = false;               // glTF UV kuralı
    tex.anisotropy = 4;
    m = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.9, metalness: 0, name: 'class_' + cls });
    classMaterials.set(cls, m);
  }
  return m;
}

/** glTF JSON'undan görsel veriyi söker (images/textures/samplers/materials) → GLTFLoader atlası dosya başına tekrar çözmez. */
async function parseStrippedGltf(url: string): Promise<GLTF> {
  const full = assetUrl(url);
  const json = await (await fetch(full)).json() as {
    images?: unknown; textures?: unknown; samplers?: unknown; materials?: unknown;
    meshes?: { primitives: { material?: number }[] }[];
  };
  delete json.images; delete json.textures; delete json.samplers; delete json.materials;
  for (const mesh of json.meshes ?? []) for (const prim of mesh.primitives) delete prim.material;
  const dir = full.slice(0, full.lastIndexOf('/') + 1);
  return new Promise<GLTF>((res, rej) => loader.parse(JSON.stringify(json), dir, res, rej));
}

/* ------------------------------------------------------------------- proplar */

const propTemplates = new Map<PropId, THREE.Object3D>();
const propLoads = new Map<PropId, Promise<THREE.Object3D>>();

/** Prop şablonu: materyalsiz parse + sınıf materyali ataması; bir kez. */
export function loadPropTemplate(id: PropId): Promise<THREE.Object3D> {
  let p = propLoads.get(id);
  if (!p) {
    p = parseStrippedGltf(PROP_URLS[id]).then((gltf) => {
      const mat = classMaterial(PROP_CLASS[id]);
      gltf.scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) { m.material = mat; m.castShadow = true; m.receiveShadow = true; }
      });
      gltf.scene.name = 'prop_' + id;
      propTemplates.set(id, gltf.scene);
      return gltf.scene;
    });
    propLoads.set(id, p);
  }
  return p;
}
export const isPropLoaded = (id: PropId): boolean => propTemplates.has(id);
/** Senkron örnekleme (şablon yüklenmiş olmalı; maç içi doğma sıcak yolda fetch beklemez). Materyal paylaşılır. */
export function createProp(id: PropId): THREE.Object3D {
  const tpl = propTemplates.get(id);
  if (!tpl) throw new Error(`prop not loaded: ${id}`);
  const root = tpl.clone(true);
  return root;
}
/** Prop yerel +Y kaydırması (holder grubu; yalnız staff 0.75 — ANIMATION §7, ITEMS.holderOffsetY). */
export const holderOffsetY = (id: PropId): number => T.ITEMS.holderOffsetY[id] ?? 0;

/* --------------------------------------------------------------- kit geometrisi */

let kitMat: THREE.MeshStandardMaterial | null = null;
/** Platformer kiti için TEK materyal + TEK doku (viewer/assets.js kitMaterial). */
export function kitMaterial(): THREE.MeshStandardMaterial {
  if (!kitMat) {
    const tex = texLoader.load(assetUrl(KIT_TEXTURE_URL));
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.flipY = false;
    tex.anisotropy = 4;
    kitMat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.9, metalness: 0, name: 'kit_platformer' });
  }
  return kitMat;
}

const kitGeometries = new Map<string, Promise<THREE.BufferGeometry>>();
/** Modeli tek BufferGeometry'ye indirger (node dönüşümleri pişirilir, primitive'ler birleşir) → InstancedMesh tek draw call. */
export function loadKitGeometry(modelId: string): Promise<THREE.BufferGeometry> {
  let p = kitGeometries.get(modelId);
  if (!p) {
    p = parseStrippedGltf(kitModelUrl(modelId)).then((gltf) => {
      gltf.scene.updateMatrixWorld(true);
      const geos: THREE.BufferGeometry[] = [];
      gltf.scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        const g = m.geometry.clone();
        g.applyMatrix4(m.matrixWorld);
        for (const attr of Object.keys(g.attributes)) if (attr !== 'position' && attr !== 'normal' && attr !== 'uv') g.deleteAttribute(attr);
        if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position!.count * 2), 2));
        if (!g.attributes.normal) g.computeVertexNormals();
        geos.push(g);
      });
      if (geos.length === 0) throw new Error(`kit model empty: ${modelId}`);
      const merged = geos.length === 1 ? geos[0]! : mergeGeometries(geos, false);
      merged.computeBoundingBox();
      merged.computeBoundingSphere();
      return merged;
    });
    kitGeometries.set(modelId, p);
  }
  return p;
}

/* --------------------------------------------------------------- clipdata.json */

/** clipdata.json fetch → parse (anim/clipData.ts parseClipData enjeksiyonla; I/O burada, ayrıştırma orada). */
export async function fetchClipData<S>(url: string, parse: (json: unknown) => S): Promise<S> {
  const res = await fetch(assetUrl(url));
  if (!res.ok) throw new Error(`clipdata fetch ${res.status}: ${url}`);
  return parse(await res.json());
}

/* -------------------------------------------------------------- toplu yükleme */

export interface PreloadRequest<S> {
  chars: readonly CharacterDef[];          // seçilen ≤ 8 karakter (tekrarlar önbellekten)
  props: readonly PropId[];                // ağırlık tablosundaki proplar (varsayılan: tümü)
  kitModels: readonly string[];            // ArenaDef.platforms[].kitModel kümesi
  clipDataUrl: string; parseClipData: (json: unknown) => S;
  progress?: LoadProgress;
  /** Dış istekler (WP7 klip setleri gibi): ilerleme sayacına eklenir. */
  extra?: readonly { promise: Promise<unknown>; label: string }[];
}
export interface PreloadResult<S> { clipData: S; kitGeometries: Map<string, THREE.BufferGeometry> }
/** Menüde "DÖVÜŞ" basılınca (ARCHITECTURE §6): Promise.all; ilerleme = tamamlanan/istek. */
export async function preloadMatch<S>(req: PreloadRequest<S>): Promise<PreloadResult<S>> {
  const prog = req.progress ?? new LoadProgress();
  const charLoads = [...new Set(req.chars.map((c) => c.url))].map((url) => prog.track(loadGLTF(url), url.slice(url.lastIndexOf('/') + 1)));
  const propLoadsP = req.props.map((id) => prog.track(loadPropTemplate(id), id));
  const kitLoads = req.kitModels.map((id) => prog.track(loadKitGeometry(id).then((g) => [id, g] as const), id));
  const clipP = prog.track(fetchClipData(req.clipDataUrl, req.parseClipData), 'clipdata');
  const extras = (req.extra ?? []).map((e) => prog.track(e.promise, e.label));
  const [, , kits, clipData] = await Promise.all([Promise.all(charLoads), Promise.all(propLoadsP), Promise.all(kitLoads), clipP, Promise.all(extras)]);
  return { clipData, kitGeometries: new Map(kits) };
}

/** Test/istatistik: önbellek boyutları. */
export const assetCacheInfo = (): { gltf: number; props: number; kits: number; materials: number } =>
  ({ gltf: gltfCache.size, props: propTemplates.size, kits: kitGeometries.size, materials: classMaterials.size });
